import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  DashboardAdminService,
  StaffDashboardResponse,
} from '@nebalkon-workspace/admin-api';
import { catchError, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class StaffDashboardService {
  readonly #api = inject(DashboardAdminService);

  readonly data = signal<StaffDashboardResponse | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly accessDenied = signal(false);

  ensureLoaded(): void {
    if (this.loading()) {
      return;
    }

    if (this.data() !== null && !this.error()) {
      return;
    }

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.accessDenied.set(false);

    this.#api
      .getDashboard()
      .pipe(
        tap((response) => {
          this.data.set(response);
          this.loading.set(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.loading.set(false);
          this.data.set(null);
          this.accessDenied.set(err.status === 403);
          this.error.set(err.status !== 403);
          return of(null);
        }),
      )
      .subscribe();
  }

  refresh(): void {
    if (this.loading()) {
      return;
    }

    this.load();
  }

  /** Loads on first visit; refreshes cached data when the home route is re-entered. */
  loadWhenActive(): void {
    if (this.loading()) {
      return;
    }

    if (this.data() !== null && !this.error()) {
      this.refresh();
      return;
    }

    this.ensureLoaded();
  }
}
